import { FormControlLabel, MenuItem, Select, Switch } from "@mui/material";
import { useSearch } from "@tanstack/react-router";
import {
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSysAccountsQuery } from "@/api/hooks/sys-account-hooks";
import { ACC_TP } from "@/components/config-modals/router-edit-modal/constants";
import { assembleRegularAccountsFiltersFns } from "@/lib/accounts-utils";
import { assembleKeyFilters, assembleSort } from "@/lib/cfg-utils";
import { assembleSearchParamsFilters } from "@/lib/common-utils";
import { ACCOUNT_GAME_STATS_COLUMN_NAMES, COMMON_REFETCH_INTERVAL } from "@/lib/constants";
import { useOrderedColumns } from "@/lib/util-hooks";
import AccountsTopToolbar from "@/module/accounts-module/components/accounts-top-toolbar";
import { useAccountsTable } from "@/module/accounts-module/hooks/accounts-table-hook";
import { useRegularAccountsTableColumns } from "@/module/accounts-module/regular-accounts-table/regular-accounts-table-columns";
import { KeyFilter, KeySort } from "@/shared/api";
import { SearchParams } from "@/types/common-types";

const requiredFilters: KeyFilter[] = [
  {
    key: "data.account_type",
    mode: "STRICT_IN",
    value: [ACC_TP.REGULAR, ACC_TP.UNREGISTERED] as unknown as object,
  },
];

const RegularAccountsTable = ({
  roomName,
  isOpen = true,
  setTotalAccountsNumber,
}: {
  roomName: string;
  isOpen?: boolean;
  setTotalAccountsNumber: (numberAccounts: number) => void;
}) => {
  const { t } = useTranslation();

  const [enableColumnDragging, setEnableColumnDragging] = useState<boolean>(false);
  const [enabledFilter, setEnabledFilter] = useState<string>("all");

  const columns = useRegularAccountsTableColumns({ room: roomName });
  //const columns = useOrderedColumns(tableColumns);

  const searchParams: SearchParams = useSearch({ strict: false });

  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "created_at", desc: true }]);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() =>
    assembleSearchParamsFilters(searchParams)
  );

  const [columnFilterFns, setColumnFilterFns] = useState<MRT_ColumnFilterFnsState>(
    assembleRegularAccountsFiltersFns({ columns, searchParams: searchParams })
  );

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  // НОВОЕ: добавляем фильтр по enabled в зависимости от выбора
  const requiredFilters: KeyFilter[] = useMemo<any>(() => {
    return [
      {
        key: "data.account_type",
        mode: "STRICT_IN",
        value: [ACC_TP.REGULAR, ACC_TP.UNREGISTERED] as unknown as object,
      },
      // Добавляем фильтр по enabled если выбрано
      ...(enabledFilter === "enabled"
        ? [{ key: "enabled", mode: "EQUALS", value: true as unknown as object }]
        : []),
      ...(enabledFilter === "disabled"
        ? [{ key: "enabled", mode: "EQUALS", value: false as unknown as object }]
        : []),
    ];
  }, [enabledFilter]);

  const filters = useMemo<Set<KeyFilter>>(
    () => assembleKeyFilters(columnFilters, columnFilterFns, requiredFilters),
    [columnFilters, columnFilterFns, requiredFilters]
  );

  const sort = useMemo<KeySort[]>(() => assembleSort(sorting), [sorting]);

  const {
    data: sysAccountsResponse,
    isError: isSysAccountsError,
    isLoading: isSysAccountsLoading,
    isRefetching: isSysAccountsRefetching,
    refetch: refetchSysAccounts,
  } = useSysAccountsQuery(
    {
      domain: roomName,
      filters: filters,
      pagination,
      sort: sort,
    },
    {
      enabled: isOpen,
      refetchInterval: COMMON_REFETCH_INTERVAL,
    }
  );

  useEffect(() => {
    !!sysAccountsResponse?.data?.totalElements &&
      setTotalAccountsNumber(sysAccountsResponse?.data?.totalElements);
  }, [sysAccountsResponse]);

  const table = useAccountsTable({
    name: "regularAccounts",
    columns: columns,
    data: sysAccountsResponse?.data.content ?? [],
    isDataLoading: isSysAccountsLoading,
    isDataRefetching: isSysAccountsRefetching,
    isDataLoadingError: isSysAccountsError,
    loadingErrorMessage: sysAccountsResponse?.statusText,
    totalElements: sysAccountsResponse?.data.totalElements,
    totalPages: sysAccountsResponse?.data.totalPages,
    pagination: pagination,
    enableColumnDragging: enableColumnDragging,
    setPagination: setPagination,
    columnFilters: columnFilters,
    setColumnFilters: setColumnFilters,
    columnFilterFns: columnFilterFns,
    setColumnFilterFns: setColumnFilterFns,
    sorting: sorting,
    setSorting: setSorting,
    columnVisibility: ACCOUNT_GAME_STATS_COLUMN_NAMES.reduce(
      (prev, cur) => ({ ...prev, [cur]: false }),
      {}
    ),
    renderToolbarCustomActions: ({ table }) => (
      <AccountsTopToolbar
        table={table}
        roomName={roomName}
        filters={filters}
        enabledFilter={{ enabledFilter, setEnabledFilter }}
        refetchAccounts={() => refetchSysAccounts()}
        totalElementsCount={sysAccountsResponse?.data.totalElements ?? 0}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <>
        <FormControlLabel
          value={enableColumnDragging}
          control={
            <Switch
              size="small"
              onClick={() => setEnableColumnDragging(!enableColumnDragging)}
            />
          }
          label={t("columnsDragging")}
          labelPlacement="start"
        />
      </>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [table, columnFilters, sorting, pagination]);

  return <MaterialReactTable table={table} />;
};

export default RegularAccountsTable;
